// src/components/Landing/motion.jsx
// Shared motion primitives for the marketing surface (landing, demo, pricing).
//
// Design rules these follow, from docs/strategy/landing-motion-plan-2026-07.md:
//  - Animate transform and opacity only. Those composite; width/height/top/
//    left/box-shadow force layout or paint on the main thread every frame.
//  - No animation library. Everything here is CSS transitions driven by a
//    single IntersectionObserver per element.
//  - prefers-reduced-motion renders the final state immediately, and a
//    catch-all in figma-refresh.css backstops anything that forgets.
//  - will-change is switched on only while a transition is in flight, then
//    released, so elements are not permanently promoted to their own GPU layer.

import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';

export const prefersReducedMotion = () =>
	typeof window !== 'undefined' &&
	window.matchMedia &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Fires once when the element scrolls into view. Returns [ref, hasEntered].
export const useInView = (options = {}) => {
	const ref = useRef(null);
	const [inView, setInView] = useState(false);
	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
			setInView(true);
			return undefined;
		}
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true);
					io.disconnect();
				}
			},
			{ threshold: 0.15, rootMargin: '0px 0px -60px 0px', ...options },
		);
		io.observe(el);
		return () => io.disconnect();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	return [ref, inView];
};

// Scroll-reveal: rises and fades in as it enters the viewport.
export const Reveal = ({ children, delay = 0, y = 26, ...rest }) => {
	const [ref, inView] = useInView();
	const [settled, setSettled] = useState(false);
	useEffect(() => {
		if (!inView) return undefined;
		const t = setTimeout(() => setSettled(true), 700 + delay * 1000);
		return () => clearTimeout(t);
	}, [inView, delay]);
	return (
		<Box
			ref={ref}
			opacity={inView ? 1 : 0}
			transform={inView ? 'none' : `translateY(${y}px)`}
			transition={`opacity 0.6s ease ${delay}s, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`}
			willChange={inView && !settled ? 'opacity, transform' : 'auto'}
			{...rest}
		>
			{children}
		</Box>
	);
};

// Staggers Reveal across siblings. 60ms reads as deliberate; past ~120ms it
// starts to feel like the page is loading slowly.
export const RevealGroup = ({ children, step = 0.06, start = 0, ...rest }) => (
	<>
		{React.Children.map(children, (child, i) => (
			<Reveal delay={start + i * step} {...rest}>
				{child}
			</Reveal>
		))}
	</>
);

// Counts up on view. The final value is what React renders, so it is present
// for crawlers, reduced-motion users and if JS never runs; intermediate frames
// are written straight to the text node to avoid ~84 re-renders per counter.
export const CountUp = ({ to, decimals = 0, suffix = '', duration = 1400, ...rest }) => {
	const [ref, inView] = useInView();
	const nodeRef = useRef(null);
	useEffect(() => {
		if (!inView || prefersReducedMotion()) return undefined;
		const el = nodeRef.current;
		if (!el) return undefined;
		let raf;
		const start = performance.now();
		const tick = (now) => {
			const p = Math.min((now - start) / duration, 1);
			const v = to * (1 - Math.pow(1 - p, 3)); // easeOutCubic
			el.textContent = v.toFixed(decimals) + suffix;
			if (p < 1) raf = requestAnimationFrame(tick);
			else el.textContent = to.toFixed(decimals) + suffix;
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [inView, to, duration, decimals, suffix]);
	return (
		<Box
			as="span"
			ref={(el) => {
				ref.current = el;
				nodeRef.current = el;
			}}
			{...rest}
		>
			{to.toFixed(decimals)}
			{suffix}
		</Box>
	);
};
